import React from "react";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { toastr } from "react-redux-toastr";
import { GridSortModel } from "@mui/x-data-grid/models/gridSortModel";
import { useSearchParams } from "react-router-dom";
import { Link } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { RootState } from "../../../../app/store";
import { FDocenteteInterface } from "../../../../interfaces/FDocenteteInterface";
import PriceComponent from "../../PriceComponent";
import { set } from "../../../../app/globalSlice";
import { searchParamToObject } from "../../../../helpers/SearchParamHelper";
import { GET } from "../../../../utils/MethodUtils";
import { objectToQuery, requestApi } from "../../../../helpers/RequestApi";
import { FDOCENTETES_URL } from "../../../../utils/UrlsUtils";
import { CAN_SEARCH_SAGE_DO_TYPE_ACHAT } from "../../../../utils/DoceneteteUtils";
import DrawerComponent from "../../DrawerComponent";
import ConsoleComptoirFilterComponent from "../../console/filter/ConsoleComptoirFilterComponent";
import { getLocaleDataGrid } from "../../../../helpers/GetLanguage";
import CopyClipboardComponent from "../../CopyClipboardComponent";
import FDocenteteConsoleComptoirComponent from "../../console/FDocenteteConsoleComptoirComponent";

export interface CountDoTypeInterface {
  count: string;
  DO_Type: string;
}

const ConsoleAchatComponent: React.FC = React.memo(() => {
  const refreshPage = useAppSelector(
    (state: RootState) => state.globalState.refreshPage
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const [init, setInit] = React.useState(false);
  const [defaultItemsPerPage] = React.useState(50);
  const [countDoType, setCountDoType] = React.useState<CountDoTypeInterface[]>(
    []
  );
  const [fDocentetes, setFDocentetes] = React.useState<
    FDocenteteInterface[] | undefined
  >(undefined);
  const [openDrawer, setOpenDrawer] = React.useState("");
  const [totalItems, setTotalItems] = React.useState(0);
  const { t, i18n } = useTranslation();
  const token = useAppSelector((state: RootState) => state.globalState.token);
  const user = useAppSelector((state: RootState) => state.globalState.user);

  const handleClose = React.useCallback(() => {
    setOpenDrawer("");
  }, []);

  const handleOpen = React.useCallback((doPiece: string) => {
    setOpenDrawer(doPiece);
  }, []);

  const getColumns = React.useCallback((): GridColDef[] => {
    return [
      {
        field: "doPiece",
        headerName: t("column.doPiece"),
        flex: 0,
        headerClassName: "background-nove",
        filterable: false,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <Link
            onClick={() => handleOpen(params.row.doPiece)}
            sx={{ cursor: "pointer" }}
          >
            {params.row.doPiece}
          </Link>
        ),
      },
      {
        field: "doType",
        headerName: t("column.doType"),
        flex: 1,
        headerClassName: "background-nove",
        filterable: false,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <>{t("word.fdocentete.doType." + params.row.doType)}</>
        ),
      },
      {
        field: "doDate",
        headerName: t("column.doDate"),
        flex: 1,
        headerClassName: "background-nove",
        filterable: false,
        renderCell: (params: GridRenderCellParams) => (
          <>
            {new Date(params.row.doDate).toLocaleString(i18n.language, {
              dateStyle: "long",
              timeZone: "Etc/UTC",
            })}
          </>
        ),
      },
      {
        field: "doTotalttc",
        headerName: t("column.doTotalttc"),
        flex: 1,
        headerClassName: "background-nove",
        filterable: false,
        renderCell: (params: GridRenderCellParams) => (
          <PriceComponent price={parseFloat(params.row.doTotalttc) * 100} />
        ),
      },
      {
        field: "doTiers",
        headerName: t("word.supplier"),
        flex: 0,
        headerClassName: "background-nove",
        filterable: false,
        renderCell: (params: GridRenderCellParams) => (
          <CopyClipboardComponent
            className="RobotoMono"
            component="span"
            text={params.row.doTiers?.ctNum ?? ""}
          />
        ),
      },
    ];
  }, [handleOpen, i18n.language, t]);
  const [columns] = React.useState<GridColDef[]>(getColumns());

  const load = React.useCallback(
    async (force: boolean = false) => {
      setInit(true);
      if (!user || (fDocentetes !== undefined && !force)) {
        dispatch(set({ refreshPage: false }));
        return;
      }
      const searchParamsObject = searchParamToObject(searchParams);
      let hasChanged = false;
      if (!searchParamsObject.hasOwnProperty("page")) {
        searchParamsObject.page = 1;
        hasChanged = true;
      }
      if (!searchParamsObject.hasOwnProperty("itemsPerPage")) {
        searchParamsObject.itemsPerPage = defaultItemsPerPage;
        hasChanged = true;
      }
      if (!force) {
        let hasOrder = false;
        for (const [key] of Object.entries(searchParamsObject)) {
          if (key.startsWith("order")) {
            hasOrder = true;
            break;
          }
        }
        if (!hasOrder) {
          searchParamsObject["order[doDate]"] = "desc";
          hasChanged = true;
        }
      }
      if (hasChanged) {
        dispatch(set({ refreshPage: false }));
        setSearchParams(searchParamsObject, {
          replace: true,
        });
        return;
      }
      const oldParams = window.location.search;
      const response = await requestApi({
        method: GET,
        path:
          FDOCENTETES_URL +
          objectToQuery({
            ...searchParamsObject,
            fDoclignesPaginate: false,
            doDomaine: 1,
            "totalDoType[]": CAN_SEARCH_SAGE_DO_TYPE_ACHAT,
            elasticSearch: 1,
          }),
        allowError: false,
        token: token,
        paginate: true,
      });
      if (response.statusCode === 200) {
        if (window.location.search !== oldParams) {
          return;
        }
        let maxId = 0;
        const newFDocentetes = [
          ...Array.from(
            Array(
              Number(searchParamsObject.itemsPerPage) *
                (Number(searchParamsObject.page) - 1)
            )
          ).map(() => {
            maxId++;
            return {
              doPiece: maxId.toString(),
            };
          }),
          ...response.content.fDocentetes,
        ];
        setFDocentetes(newFDocentetes);
        setTotalItems(response.content.totalItems);
        setCountDoType(response.content.total);
      } else if (response.statusCode === 401) {
        toastr.info(t("word.info"), t("error.reconnect"));
      } else {
        toastr.error(t("word.error"), t("error.tryAgain"));
      }
      dispatch(set({ refreshPage: false }));
    },
    [
      defaultItemsPerPage,
      dispatch,
      fDocentetes,
      searchParams,
      setSearchParams,
      t,
      token,
      user,
    ]
  );

  const onPageSizeChange = React.useCallback(
    (pageSize: number) => {
      if (!init) {
        return;
      }
      setFDocentetes(undefined);
      const searchParamsObject = searchParamToObject(searchParams);
      searchParamsObject.itemsPerPage = pageSize;
      searchParamsObject.page = 1;
      setSearchParams(searchParamsObject, {
        replace: true,
      });
    },
    [init, searchParams, setSearchParams]
  );

  const onPageChange = React.useCallback(
    (page: number) => {
      if (!init) {
        return;
      }
      setFDocentetes(undefined);
      const searchParamsObject = searchParamToObject(searchParams);
      searchParamsObject.page = page + 1;
      setSearchParams(searchParamsObject, {
        replace: true,
      });
    },
    [init, searchParams, setSearchParams]
  );

  const onSortModelChange = React.useCallback(
    (model: GridSortModel) => {
      if (!init) {
        return;
      }
      const searchParamsObject = searchParamToObject(searchParams);
      for (const [key] of Object.entries(searchParamsObject)) {
        if (key.startsWith("order")) {
          delete searchParamsObject[key];
        }
      }
      for (const sort of model) {
        // @ts-ignore
        searchParamsObject["order[" + sort.field + "]"] = sort.sort;
      }
      setSearchParams(searchParamsObject, {
        replace: true,
      });
    },
    [init, searchParams, setSearchParams]
  );

  const getSortModel = React.useCallback(() => {
    const searchParamsObject = searchParamToObject(searchParams);
    const result: any[] = [];
    for (const [key, value] of Object.entries(searchParamsObject)) {
      if (key.startsWith("order")) {
        let field: string[] | string = key.split("[");
        field = field[1].replace("]", "");
        result.push({
          field: field,
          sort: value,
        });
      }
    }
    return result;
  }, [searchParams]);

  React.useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (init && refreshPage) {
      load(true);
    }
  }, [refreshPage, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!user) {
      setSearchParams(
        {},
        {
          replace: true,
        }
      );
    }
    if (init && !refreshPage) {
      dispatch(set({ refreshPage: true }));
    }
  }, [searchParams, user?.userIdentifier]); // eslint-disable-line react-hooks/exhaustive-deps

  const searchParamsObject = searchParamToObject(searchParams);
  return (
    <>
      <DrawerComponent
        open={openDrawer !== ""}
        onClose={handleClose}
        handleDrawerClose={handleClose}
        drawerwidth={window.innerWidth}
        content={
          <FDocenteteConsoleComptoirComponent
            doPiece={openDrawer}
            handleClose={handleClose}
          />
        }
      />
      <ConsoleComptoirFilterComponent countDoType={countDoType} doDomaine={1} />
      {searchParamsObject.itemsPerPage && searchParamsObject.page && (
        // https://mui.com/x/react-data-grid/components/#pagination
        <DataGrid
          initialState={{
            sorting: {
              sortModel: getSortModel(),
            },
          }}
          getRowId={(row) => row.doPiece}
          loading={refreshPage}
          rows={refreshPage ? [] : fDocentetes ?? []}
          onSortModelChange={onSortModelChange}
          sortingMode="server"
          page={Number(searchParamsObject.page) - 1}
          rowsPerPageOptions={[10, 25, 50]}
          pageSize={Number(searchParamsObject.itemsPerPage)}
          onPageSizeChange={onPageSizeChange}
          onPageChange={onPageChange}
          rowCount={totalItems}
          columns={columns}
          autoHeight={true}
          disableExtendRowFullWidth={true}
          localeText={getLocaleDataGrid(i18n.language)}
        />
      )}
    </>
  );
});

export default ConsoleAchatComponent;
